import { getDB, type KvValues } from './schema';
import type { StreamConfig } from '../types/stream';
import { readCredentialBackup, saveCredentialBackup } from './credentialBackup';

// Typed key/value access backed by the IndexedDB `kv` store. Replaces the
// Android SharedPreferences (Prefs.kt).

const DEFAULT_CONFIG: StreamConfig = {
  refresh_rate: 120,
  brightness: 100,
  rotation: 0,
  volume: 0,
  low_memory_mode: false,
  auto_reboot: false,
  reboot_time: null,
  update_preference: '',
};

async function getKv<K extends keyof KvValues>(
  key: K,
  fallback: KvValues[K],
): Promise<KvValues[K]> {
  const db = await getDB();
  const value = (await db.get('kv', key)) as KvValues[K] | undefined;
  return value === undefined ? fallback : value;
}

async function setKv<K extends keyof KvValues>(key: K, value: KvValues[K]): Promise<void> {
  const db = await getDB();
  await db.put('kv', value, key);
}

export const prefs = {
  getToken: () => getKv('device_token', ''),
  setToken: async (v: string) => {
    saveCredentialBackup({ token: v });
    await setKv('device_token', v);
  },

  getUuid: () => getKv('device_uuid', ''),
  setUuid: async (v: string) => {
    saveCredentialBackup({ uuid: v });
    await setKv('device_uuid', v);
  },

  getDeviceId: () => getKv('device_id', 0),
  setDeviceId: async (v: number) => {
    saveCredentialBackup({ deviceId: v });
    await setKv('device_id', v);
  },

  getSequence: () => getKv('sequence', 0),
  setSequence: (v: number) => setKv('sequence', v),

  getConfig: () => getKv('config', DEFAULT_CONFIG),
  setConfig: (v: StreamConfig) => setKv('config', v),

  getLastLogTs: () => getKv('last_log_ts', 0),
  setLastLogTs: (v: number) => setKv('last_log_ts', v),

  getRotation: () => getKv('display_rotation', 0),
  setRotation: (v: number) => setKv('display_rotation', v),
};

// If IndexedDB lost the device identity (e.g. its LevelDB was discarded after an
// unclean power-off) but a localStorage backup still exists, reseed the kv store
// so the device stays paired and only needs to re-sync content instead of
// dropping back to the pairing screen. Call this once at boot before reading the
// token. Returns true if credentials were restored.
export async function recoverCredentialsIfNeeded(): Promise<boolean> {
  const existing = await getKv('device_token', '');
  if (existing) return false;

  const backup = readCredentialBackup();
  if (!backup) return false;

  await setKv('device_token', backup.token);
  if (backup.uuid) await setKv('device_uuid', backup.uuid);
  if (backup.deviceId) await setKv('device_id', backup.deviceId);
  console.warn('[prefs] restored device credentials from localStorage backup after IndexedDB loss');
  return true;
}

export { DEFAULT_CONFIG };
